import type React from 'react';
import { useState, useCallback } from 'react';

import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';

import { INT_PARAMETER, PARAMETER } from '../../../../../../constants/parameter-name-for-update';
import { messages } from './messages';

import style from '../style.module.less';
import sharedStyles from '../../../../../../style.module.less';

export const DateTypes = {
  OrderDate: 'OrderDate',
  DeliverByDate: 'DeliverByDate',
  CurrentDate: 'CurrentDate',
} as const;

export const SKIP_REPROCESS_SUFFIX = ':skipReprocess';

type DateType = (typeof DateTypes)[keyof typeof DateTypes];

const dateTypeMessageMap: Record<DateType, React.ReactNode> = {
  [DateTypes.OrderDate]: <FormattedMessage {...messages.afterOrderDate} />,
  [DateTypes.DeliverByDate]: <FormattedMessage {...messages.beforeDeliverByDate} />,
  [DateTypes.CurrentDate]: <FormattedMessage {...messages.afterCurrentDate} />,
};

type SetShipDateProps = {
  inputValueProps: {
    parameter?: string;
    intParameter?: number;
  };
  onChange: (value: { [key: string]: string | number }) => void;
  hasError: boolean;
  label: React.ReactNode;
};

const parseParameter = (parameter?: string): DateType => {
  if (!parameter) {
    return DateTypes.OrderDate;
  }
  return parameter.replace(SKIP_REPROCESS_SUFFIX, '') as DateType;
};

const SetShipDate = ({ inputValueProps, onChange, hasError, label }: SetShipDateProps) => {
  const { parameter, intParameter } = inputValueProps;
  const [numberOfDays, setNumberOfDays] = useState(intParameter ?? 0);
  const [selectedDateType, setSelectedDateType] = useState<DateType>(parseParameter(parameter));

  const onChangeDays = useCallback((newValue: number | undefined) => {
    setNumberOfDays(newValue ?? 0);
  }, []);

  const onBlurDays = useCallback(() => {
    onChange({
      [INT_PARAMETER]: numberOfDays,
    });
  }, [onChange, numberOfDays]);

  const onArrowButtonPress = useCallback(
    (newValue: number) => {
      setNumberOfDays(newValue);
      onChange({
        [INT_PARAMETER]: newValue,
      });
    },
    [onChange],
  );

  const onChangeDateType = useCallback(
    (newDateType: DateType) => {
      setSelectedDateType(newDateType);
      onChange({
        [PARAMETER]: newDateType,
      });
    },
    [onChange],
  );

  const daysInput = (
    <NumberInput
      className={classnames(style['days-input'], { [style.error]: hasError })}
      onArrowButtonPress={onArrowButtonPress}
      onBlur={onBlurDays}
      onChange={onChangeDays}
      value={numberOfDays}
    />
  );

  const dateTypeDropdown = (
    <DropdownMenu className={style['date-type-dropdown']} dropdownMenuShouldHaveWidthOfButton dropdownMenuType={DropdownMenuType.ComboBox}>
      <DropdownMenuToggler className={sharedStyles['dropdown-toggler']}>{dateTypeMessageMap[selectedDateType]}</DropdownMenuToggler>
      <DropdownMenuItem onClick={() => onChangeDateType(DateTypes.OrderDate)}>{dateTypeMessageMap[DateTypes.OrderDate]}</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onChangeDateType(DateTypes.DeliverByDate)}>
        {dateTypeMessageMap[DateTypes.DeliverByDate]}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onChangeDateType(DateTypes.CurrentDate)}>
        {dateTypeMessageMap[DateTypes.CurrentDate]}
      </DropdownMenuItem>
    </DropdownMenu>
  );

  return (
    <div className={style['multi-control-row']}>
      <div>{label}</div>
      <Text
        className={style['set-days-around-date']}
        element='div'
        formattedMessageValues={{
          daysInput: () => daysInput,
          dateTypeDropdown: () => dateTypeDropdown,
          numberOfDays,
        }}
        textType={TextType.Paragraph}
        value={messages.daysAroundDate}
      />
    </div>
  );
};

export default SetShipDate;
